import { useCallback, useEffect, useState } from 'react'

import axios, { AxiosError } from 'axios'
import config from 'config'

import { defaultLosJsonHeaders } from '../../api/requestHeaders'
import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'

export type DealOwnerUserOption = {
    id: number
    // Backend-formatted display string, e.g. "Alex Sample (asample)".
    name: string
}

type CandidateResponseItem = {
    id: number
    full_name: string
    username: string
}

// Legacy / seed accounts sometimes have a blank `full_name`; fall back to
// `username` alone so those rows still render something readable.
const composeDisplayName = ({
    full_name,
    username,
}: Pick<CandidateResponseItem, 'full_name' | 'username'>) =>
    full_name ? `${full_name} (${username})` : username

// Alphabetize by display name.
function sortByName(users: DealOwnerUserOption[]): DealOwnerUserOption[] {
    return [...users].sort((a, b) => a.name.localeCompare(b.name))
}

// GET /api/v3/users/deal-owner-candidates/
// - Deal Owner (existing app): pass application_uuid; backend scopes to that
//   deal's dealership chain and enforces the requester has access to the app.
// - Finance Manager (new-app form): no application_uuid; backend returns
//   candidates from the requester's own dealership chain.
const useDealershipUsersWithUsername = (applicationUuid?: string) => {
    const [users, setUsers] = useState<DealOwnerUserOption[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [reloadKey, setReloadKey] = useState(0)

    const retry = useCallback(() => {
        setReloadKey((k) => k + 1)
    }, [])

    useEffect(() => {
        const abortController = new AbortController()

        const fetchUsers = async () => {
            setLoading(true)
            setError(null)

            try {
                const params = new URLSearchParams()

                if (applicationUuid) {
                    params.set('application_uuid', applicationUuid)
                }

                const query = params.toString()

                const url = `${config.API_BASE_URL}/api/v3/users/deal-owner-candidates/${
                    query ? `?${query}` : ''
                }`

                const { data } = await axios.get<CandidateResponseItem[]>(url, {
                    withCredentials: true,
                    headers: defaultLosJsonHeaders(),
                    signal: abortController.signal,
                })

                const mapped: DealOwnerUserOption[] = (data ?? []).map((u) => ({
                    id: u.id,
                    name: composeDisplayName(u),
                }))

                setUsers(sortByName(mapped))
            } catch (caught) {
                // Tests often mock axios without `isCancel`; guard defensively.
                if (axios.isCancel?.(caught)) return

                const axiosError = caught as AxiosError

                OctaneLogger.axiosError(
                    `Failed to get Deal Owner candidates: ${axiosError.message}`,
                    axiosError,
                )

                setError(axiosError)
            } finally {
                setLoading(false)
            }
        }

        fetchUsers()

        return () => abortController.abort()
    }, [applicationUuid, reloadKey])

    return { users, loading, error, retry }
}

export default useDealershipUsersWithUsername
